import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import type {
  AlertSchedule,
  AlertSendInfo,
  DefaultApi,
  MSTeamsChannel,
  SlackChannel,
  SlackUser,
  WebDashboardSubscriptionConfig,
  WebDashboardSubscriptionInfo,
} from "@coeff/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";

import { QueryCacheKey } from "../../../api/queryClient";
import { ConfirmBox } from "../../../components";
import { useMSTeamsOAuth } from "../../../hooks/useMSTeamsOAuth";
import { useSlackOAuth } from "../../../hooks/useSlackOAuth";
import { getTimeZone, useApiContext } from "../../../utils";
import {
  createWebDashboardSubscription,
  deleteWebDashboardSubscription,
  fetchWebDashboardSubscriptions,
  patchWebDashboardSubscription,
  updateWebDashboardSubscription,
} from "../webDashboard";

import { SubscriptionForm } from "./form";
import { SubscriptionListItem } from "./primitives";
import { buildScheduleLabel, DashboardSubscription, SubscriptionDestination } from "./types";

const toApiSchedule = (sub: Omit<DashboardSubscription, "id">): AlertSchedule => {
  // Preserve the saved timezone when editing an existing subscription so that
  // the hour the user sees in the dropdown matches what gets written to the API.
  // For new subscriptions scheduleTzName is undefined, so we fall back to the
  // user's current local timezone.
  const tzName = sub.scheduleTzName ?? getTimeZone();
  if (sub.frequency === "hourly") {
    return {
      repeat_type: "hourly",
      every_n_hours: 1,
      tz_name: tzName,
    };
  }
  if (sub.frequency === "daily") {
    return {
      repeat_type: "daily",
      hour: sub.hourIndex ?? 9,
      tz_name: tzName,
    };
  }
  if (sub.frequency === "weekly") {
    const raw = sub.weekdays?.length ? sub.weekdays : [5];
    const days_of_week = [...new Set(raw)].sort((a, b) => a - b);
    return {
      repeat_type: "weekly",
      hour: sub.hourIndex ?? 9,
      days_of_week,
      tz_name: tzName,
    };
  }
  const daysOfMonth = (sub.daysOfMonth || "")
    .split(",")
    .map(day => Number(day.trim()))
    .filter(day => Number.isInteger(day) && day >= 1 && day <= 31);
  return {
    repeat_type: "monthly",
    hour: sub.hourIndex ?? 9,
    days_of_month: daysOfMonth.length > 0 ? daysOfMonth : [1],
    tz_name: tzName,
  };
};

const extractRecipientsByType = (
  destinations: SubscriptionDestination[],
  type: SubscriptionDestination["type"]
) =>
  destinations
    .filter(destination => destination.type === type)
    .flatMap(destination => destination.recipients);

const toApiSendInfo = async (
  apiClient: DefaultApi,
  destinations: SubscriptionDestination[]
): Promise<AlertSendInfo> => {
  const emailAddresses = Array.from(
    new Set(
      extractRecipientsByType(destinations, "email")
        .map(email => email.trim())
        .filter(Boolean)
    )
  );
  const slackRecipients = Array.from(
    new Set(
      extractRecipientsByType(destinations, "slack")
        .map(channel => channel.trim())
        .filter(Boolean)
    )
  );
  const teamsRecipients = Array.from(
    new Set(
      extractRecipientsByType(destinations, "teams")
        .map(channel => channel.trim())
        .filter(Boolean)
    )
  );

  const slackChannels: SlackChannel[] = [];
  const slackUsersResolved: SlackUser[] = [];
  if (slackRecipients.length > 0) {
    const response = await apiClient.getSlackChannelsAndUsers();
    const channels = response.data?.slack_channels ?? [];
    const users = response.data?.slack_users ?? [];
    const channelsByName = new Map<string, SlackChannel>(
      channels.map(channel => [channel.channel_name.toLowerCase(), channel])
    );
    const usersByName = new Map<string, SlackUser>(users.map(u => [u.user_name.toLowerCase(), u]));

    for (const raw of slackRecipients) {
      const t = raw.trim();
      if (!t) {
        continue;
      }

      if (t.startsWith("@")) {
        const uname = t.slice(1).toLowerCase();
        const u = usersByName.get(uname);
        if (u) {
          slackUsersResolved.push({
            user_id: u.user_id,
            user_name: u.user_name,
            user_real_name: u.user_real_name,
          });
        } else {
          slackUsersResolved.push({
            user_id: uname,
            user_name: t.slice(1),
          });
        }
        continue;
      }

      const chMatch =
        channelsByName.get(t.toLowerCase()) ||
        channelsByName.get(t.replace(/^#/, "").toLowerCase());
      if (chMatch) {
        slackChannels.push({
          channel_id: chMatch.channel_id,
          channel_name: chMatch.channel_name,
          is_private: chMatch.is_private,
        });
        continue;
      }

      const uMatch = usersByName.get(t.toLowerCase());
      if (uMatch) {
        slackUsersResolved.push({
          user_id: uMatch.user_id,
          user_name: uMatch.user_name,
          user_real_name: uMatch.user_real_name,
        });
        continue;
      }

      slackChannels.push({
        channel_id: t.replace(/^#/, ""),
        channel_name: t.replace(/^#/, ""),
      });
    }
  }

  let teamsChannels: MSTeamsChannel[] = [];
  if (teamsRecipients.length > 0) {
    const response = await apiClient.getMSTeamsTeamsAndChannels();
    const channels = response.data?.teams_and_channels ?? [];
    teamsChannels = teamsRecipients.map(recipient => {
      const [teamName = "", channelName = ""] = recipient.split(" / ").map(token => token.trim());
      const match = channels.find(
        channel => channel.team_name === teamName && channel.channel_name === channelName
      );
      return {
        team_id: match?.team_id ?? teamName,
        team_name: match?.team_name ?? teamName,
        channel_id: match?.channel_id ?? channelName,
        channel_name: match?.channel_name ?? channelName,
      };
    });
  }

  return {
    email_enabled: emailAddresses.length > 0,
    email_addresses: emailAddresses,
    slack_enabled: slackChannels.length > 0 || slackUsersResolved.length > 0,
    slack_channels: slackChannels,
    slack_users: slackUsersResolved,
    ms_teams_enabled: teamsChannels.length > 0,
    ms_teams_channels: teamsChannels,
    ms_teams_users: [],
  };
};

const toApiSubscriptionConfig = async (
  apiClient: DefaultApi,
  sub: Omit<DashboardSubscription, "id">,
  blocks: WebDashboardContentSpecBlocksInner[]
): Promise<WebDashboardSubscriptionConfig> => {
  return {
    name: sub.scheduleLabel || "Dashboard subscription",
    send_info: await toApiSendInfo(apiClient, sub.destinations),
    schedule: toApiSchedule(sub),
    scope: sub.scope === "specific" ? "blocks" : "full_dashboard",
    block_ids:
      sub.scope === "specific" ? normalizeSubscriptionBlockIds(sub.blockIds ?? [], blocks) : [],
    is_active: sub.paused !== true,
  };
};

const normalizeSubscriptionBlockIds = (
  blockIds: string[],
  blocks: WebDashboardContentSpecBlocksInner[]
): string[] => {
  const realBlockIds = new Set(blocks.map(block => block.block_id).filter(Boolean));
  const normalizedIds = blockIds.map(blockId => {
    if (realBlockIds.has(blockId)) {
      return blockId;
    }

    const legacySyntheticIdMatch = /^block_(\d+)$/.exec(blockId);
    if (legacySyntheticIdMatch) {
      const blockIndex = Number(legacySyntheticIdMatch[1]);
      return blocks[blockIndex]?.block_id ?? blockId;
    }

    return blockId;
  });

  const seen = new Set<string>();
  return normalizedIds.filter(blockId => {
    if (!realBlockIds.has(blockId) || seen.has(blockId)) {
      return false;
    }
    seen.add(blockId);
    return true;
  });
};

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  v !== null && typeof v === "object" ? (v as Record<string, unknown>) : undefined;

const extractSendInfoFromConfig = (
  config: WebDashboardSubscriptionConfig | undefined
): AlertSendInfo | undefined => {
  const c = asRecord(config);
  if (!c) {
    return undefined;
  }
  const raw = asRecord(c.send_info ?? c.sendInfo);
  if (!raw) {
    return undefined;
  }

  const emailAddressesRaw = raw.email_addresses ?? raw.emailAddresses;
  const emailRecipients = Array.isArray(emailAddressesRaw)
    ? (emailAddressesRaw as unknown[]).map(String).filter(Boolean)
    : [];

  const slackRaw = (raw.slack_channels ?? raw.slackChannels) as unknown[] | undefined;
  const slack_channels: SlackChannel[] = (slackRaw ?? []).map((ch: unknown) => {
    const o = asRecord(ch) ?? {};
    return {
      channel_id: String(o.channel_id ?? o.channelId ?? ""),
      channel_name: String(o.channel_name ?? o.channelName ?? ""),
      is_private: (o.is_private ?? o.isPrivate) as boolean | undefined,
    };
  });

  const teamsRaw = (raw.ms_teams_channels ?? raw.msTeamsChannels) as unknown[] | undefined;
  const ms_teams_channels: MSTeamsChannel[] = (teamsRaw ?? []).map((ch: unknown) => {
    const o = asRecord(ch) ?? {};
    return {
      team_id: String(o.team_id ?? o.teamId ?? ""),
      team_name: String(o.team_name ?? o.teamName ?? ""),
      channel_id: String(o.channel_id ?? o.channelId ?? ""),
      channel_name: String(o.channel_name ?? o.channelName ?? ""),
    };
  });

  return {
    email_enabled: Boolean(raw.email_enabled ?? raw.emailEnabled),
    email_addresses: emailRecipients,
    slack_enabled: Boolean(raw.slack_enabled ?? raw.slackEnabled),
    slack_channels,
    slack_users: (raw.slack_users ?? raw.slackUsers ?? []) as any[],
    ms_teams_enabled: Boolean(raw.ms_teams_enabled ?? raw.msTeamsEnabled),
    ms_teams_channels,
    ms_teams_users: (raw.ms_teams_users ?? raw.msTeamsUsers ?? []) as any[],
  };
};

const toDashboardSubscription = (
  subscription: WebDashboardSubscriptionInfo
): DashboardSubscription => {
  const config = subscription.config;
  const schedule = config?.schedule;
  const sendInfo = extractSendInfoFromConfig(config);
  const destinations: SubscriptionDestination[] = [];

  const emailRecipients = sendInfo?.email_addresses ?? [];
  if (emailRecipients.length > 0) {
    destinations.push({ type: "email", recipients: emailRecipients });
  }
  const slackChannelNames = (sendInfo?.slack_channels ?? [])
    .map(channel => channel.channel_name)
    .filter(Boolean);
  const slackUserRecipients = (sendInfo?.slack_users ?? [])
    .map((u: unknown) => {
      const o = asRecord(u) ?? {};
      const name = String(o.user_name ?? o.userName ?? "").trim();
      if (!name) {
        return "";
      }
      return name.startsWith("@") ? name : `@${name}`;
    })
    .filter(Boolean);
  const slackRecipients = [...slackChannelNames, ...slackUserRecipients];
  if (slackRecipients.length > 0) {
    destinations.push({ type: "slack", recipients: slackRecipients });
  }
  const teamsRecipients = (sendInfo?.ms_teams_channels ?? [])
    .map(channel => `${channel.team_name} / ${channel.channel_name}`)
    .filter(line => line.trim() !== "/");
  if (teamsRecipients.length > 0) {
    destinations.push({ type: "teams", recipients: teamsRecipients });
  }

  const dow = schedule?.days_of_week ?? [];
  const baseSub: DashboardSubscription = {
    id: subscription.web_dashboard_subscription_id,
    frequency: schedule?.repeat_type ?? "daily",
    hourIndex: schedule?.hour ?? 9,
    scheduleTzName: schedule?.tz_name,
    weekdays: dow.length ? [...dow] : [5],
    daysOfMonth: (schedule?.days_of_month ?? []).join(", "),
    scope: config?.scope === "blocks" ? "specific" : "entire",
    blockIds: config?.block_ids ?? [],
    includeAiSummary: true,
    destinations,
    scheduleLabel: "",
    paused: config?.is_active === false,
    lastRunDt: subscription.last_run_dt,
    lastRunFailed: subscription.last_run_status === "failed",
    lastRunErrorMessage: subscription.last_run_error_message,
  };

  return { ...baseSub, scheduleLabel: buildScheduleLabel(baseSub) };
};

interface SubscribeDashboardModalProps {
  open: boolean;
  onClose: () => void;
  dashboardId?: string;
  blocks?: WebDashboardContentSpecBlocksInner[];
  userEmail?: string | null;
  targetDocument?: Document;
}

export const SubscribeDashboardModal = React.forwardRef<
  HTMLDivElement,
  SubscribeDashboardModalProps
>(({ open, onClose, dashboardId, blocks = [], userEmail, targetDocument }, ref) => {
  const listMenuDocument = targetDocument ?? document;
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState<{ mode: "new" | "edit"; id?: string } | null>(null);
  const [deleteConfirmSubscriptionId, setDeleteConfirmSubscriptionId] = useState<string | null>(
    null
  );
  const [slackAuthSuccessTick, setSlackAuthSuccessTick] = useState(0);
  const [teamsAuthSuccessTick, setTeamsAuthSuccessTick] = useState(0);
  const {
    isAuthorized: isSlackAuthorized,
    isAuthInProgress: isSlackAuthInProgress,
    startOAuth: onStartSlackOAuth,
    cancelOAuth: onCancelSlackOAuth,
  } = useSlackOAuth({
    userEmail: userEmail || "",
    onSuccess: () => setSlackAuthSuccessTick(prev => prev + 1),
    onFailure: () => {},
  });
  const {
    isAuthorized: isTeamsAuthorized,
    isAuthInProgress: isTeamsAuthInProgress,
    startOAuth: onStartTeamsOAuth,
    cancelOAuth: onCancelTeamsOAuth,
  } = useMSTeamsOAuth({
    userEmail: userEmail || "",
    onSuccess: () => setTeamsAuthSuccessTick(prev => prev + 1),
    onFailure: () => {},
  });

  const subscriptionsQuery = useQuery(
    [QueryCacheKey.WEB_DASHBOARD_SUBSCRIPTIONS, dashboardId],
    () => fetchWebDashboardSubscriptions(apiClient, dashboardId as string),
    { enabled: open && !!dashboardId }
  );
  const subscriptions = useMemo(
    () =>
      (subscriptionsQuery.data?.subscriptions ?? []).map(subscription =>
        toDashboardSubscription(subscription)
      ),
    [subscriptionsQuery.data]
  );
  const hasSlackSubscriptions = useMemo(
    () => subscriptions.some(sub => sub.destinations.some(d => d.type === "slack")),
    [subscriptions]
  );

  const createSubscriptionMutation = useMutation(async (sub: Omit<DashboardSubscription, "id">) => {
    const config = await toApiSubscriptionConfig(apiClient, sub, blocks);
    return createWebDashboardSubscription(apiClient, dashboardId as string, { config });
  });

  const updateSubscriptionMutation = useMutation(
    async (params: { subscriptionId: string; sub: Omit<DashboardSubscription, "id"> }) => {
      const config = await toApiSubscriptionConfig(apiClient, params.sub, blocks);
      return updateWebDashboardSubscription(
        apiClient,
        dashboardId as string,
        params.subscriptionId,
        {
          name: config.name,
          send_info: config.send_info,
          schedule: config.schedule,
          scope: config.scope,
          block_ids: config.block_ids,
          is_active: config.is_active,
        }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_SUBSCRIPTIONS, dashboardId]);
      },
    }
  );

  const togglePauseMutation = useMutation(
    (params: { subscriptionId: string; is_active: boolean }) =>
      patchWebDashboardSubscription(apiClient, dashboardId as string, params.subscriptionId, {
        is_active: params.is_active,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_SUBSCRIPTIONS, dashboardId]);
      },
    }
  );

  const deleteSubscriptionMutation = useMutation(
    (subscriptionId: string) =>
      deleteWebDashboardSubscription(apiClient, dashboardId as string, subscriptionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_SUBSCRIPTIONS, dashboardId]);
      },
    }
  );

  const editingSubscription =
    formState?.mode === "edit" ? subscriptions.find(s => s.id === formState.id) : undefined;

  const handleAdd = () => setFormState({ mode: "new" });
  const handleEdit = (id: string) => setFormState({ mode: "edit", id });

  const subscriptionsLoadedEmpty = subscriptionsQuery.isSuccess && subscriptions.length === 0;

  const handleCloseForm = () => {
    if (subscriptionsLoadedEmpty) {
      onClose();
      return;
    }
    setFormState(null);
  };

  const handleSaveNew = (data: Omit<DashboardSubscription, "id">) => {
    if (!dashboardId || formState?.mode === "edit") {
      return;
    }
    void createSubscriptionMutation.mutateAsync(data).then(async () => {
      await queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_SUBSCRIPTIONS, dashboardId]);
      setFormState(null);
    });
  };

  const handleSaveEdit = (data: Omit<DashboardSubscription, "id">) => {
    if (!dashboardId || !formState?.id) {
      return;
    }
    void updateSubscriptionMutation
      .mutateAsync({ subscriptionId: formState.id, sub: data })
      .then(() => setFormState(null));
  };

  const handleRequestDeleteSubscription = (subscriptionId: string) => {
    setDeleteConfirmSubscriptionId(subscriptionId);
  };

  const handleCancelDeleteSubscription = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setDeleteConfirmSubscriptionId(null);
  };

  const handleConfirmDeleteSubscription = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const subscriptionId = deleteConfirmSubscriptionId;
    if (!subscriptionId || !dashboardId || deleteSubscriptionMutation.isLoading) {
      return;
    }
    void deleteSubscriptionMutation.mutateAsync(subscriptionId).then(() => {
      setDeleteConfirmSubscriptionId(null);
      setFormState(prev => (prev?.mode === "edit" && prev.id === subscriptionId ? null : prev));
    });
  };

  useEffect(() => {
    if (!open) {
      setFormState(null);
      setDeleteConfirmSubscriptionId(null);
    }
  }, [open]);

  useEffect(() => {
    if (
      formState?.mode === "edit" &&
      subscriptionsQuery.isSuccess &&
      !subscriptionsQuery.isFetching &&
      !editingSubscription
    ) {
      setFormState(null);
    }
  }, [
    editingSubscription,
    formState?.mode,
    subscriptionsQuery.isFetching,
    subscriptionsQuery.isSuccess,
  ]);

  if (!open) {
    return null;
  }

  const showNewForm = formState?.mode === "new" || (formState === null && subscriptionsLoadedEmpty);
  const showEditForm = formState?.mode === "edit" && !!editingSubscription;

  return (
    <div
      ref={ref}
      className="absolute top-full right-0 mt-2 z-[300] flex w-[530px] max-w-[calc(100vw-2rem)] flex-col rounded-lg bg-main-background shadow-[0_12px_48px_rgba(0,0,0,0.15)]"
      role="dialog"
      aria-label={showNewForm || showEditForm ? "Subscription" : "Subscriptions"}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      {showNewForm ? (
        <SubscriptionForm
          key="new-subscription"
          portalDocument={listMenuDocument}
          title="New Subscription"
          blocks={blocks}
          onSave={handleSaveNew}
          onCancel={handleCloseForm}
          onClose={onClose}
          showBack={!subscriptionsLoadedEmpty}
          isSaving={createSubscriptionMutation.isLoading}
          isSlackAuthorized={isSlackAuthorized}
          isSlackAuthInProgress={isSlackAuthInProgress}
          onStartSlackOAuth={onStartSlackOAuth}
          onCancelSlackOAuth={onCancelSlackOAuth}
          isTeamsAuthorized={isTeamsAuthorized}
          isTeamsAuthInProgress={isTeamsAuthInProgress}
          onStartTeamsOAuth={onStartTeamsOAuth}
          onCancelTeamsOAuth={onCancelTeamsOAuth}
          slackAuthSuccessTick={slackAuthSuccessTick}
          teamsAuthSuccessTick={teamsAuthSuccessTick}
        />
      ) : showEditForm ? (
        <SubscriptionForm
          key={`edit-subscription-${editingSubscription.id}`}
          portalDocument={listMenuDocument}
          title="Edit Subscription"
          initialState={editingSubscription}
          blocks={blocks}
          onSave={handleSaveEdit}
          onCancel={handleCloseForm}
          onClose={onClose}
          showBack
          isSaving={updateSubscriptionMutation.isLoading}
          isPaused={editingSubscription.paused === true}
          onPause={() =>
            togglePauseMutation.mutate({
              subscriptionId: editingSubscription.id,
              is_active: editingSubscription.paused === true ? true : false,
            })
          }
          onDelete={() => handleRequestDeleteSubscription(editingSubscription.id)}
          isSlackAuthorized={isSlackAuthorized}
          isSlackAuthInProgress={isSlackAuthInProgress}
          onStartSlackOAuth={onStartSlackOAuth}
          onCancelSlackOAuth={onCancelSlackOAuth}
          isTeamsAuthorized={isTeamsAuthorized}
          isTeamsAuthInProgress={isTeamsAuthInProgress}
          onStartTeamsOAuth={onStartTeamsOAuth}
          onCancelTeamsOAuth={onCancelTeamsOAuth}
          slackAuthSuccessTick={slackAuthSuccessTick}
          teamsAuthSuccessTick={teamsAuthSuccessTick}
        />
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          {/* List header */}
          <div className="flex items-center justify-between px-5 pt-5">
            <h2 className="text-base font-semibold text-main-foreground">Subscriptions</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-main-muted-foreground hover:text-main-foreground rounded-lg hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
              aria-label="Close"
            >
              <span className="material-icons !text-xl">close</span>
            </button>
          </div>

          {/* List body */}
          <div className="overflow-y-auto flex-1 min-h-0 flex flex-col gap-2 px-4 py-2">
            {subscriptionsQuery.isLoading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <span className="material-icons !text-2xl text-main-muted-foreground animate-spin">
                  refresh
                </span>
                <p className="text-sm text-main-muted-foreground">Loading subscriptions...</p>
              </div>
            ) : subscriptionsQuery.isError ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <span className="material-icons !text-2xl text-red-500">error_outline</span>
                <p className="text-sm text-red-600">
                  Unable to load subscriptions. Please try again.
                </p>
              </div>
            ) : (
              <>
                {hasSlackSubscriptions && isSlackAuthorized === false && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                    <span className="material-icons text-red-500 !text-base shrink-0 mt-0.5">
                      error
                    </span>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-main-foreground">
                        Reconnect Slack
                      </span>
                      <p className="text-sm text-main-foreground">
                        You need to reconnect your Slack account to Coefficient
                      </p>
                      <button
                        type="button"
                        className="self-start text-sm font-semibold text-blue-600 hover:underline cursor-pointer"
                        onClick={onStartSlackOAuth}
                      >
                        Reconnect Slack
                      </button>
                    </div>
                  </div>
                )}
                {subscriptions.map(sub => (
                  <SubscriptionListItem
                    key={sub.id}
                    subscription={sub}
                    targetDocument={listMenuDocument}
                    onEdit={() => handleEdit(sub.id)}
                    onPause={() =>
                      togglePauseMutation.mutate({
                        subscriptionId: sub.id,
                        is_active: sub.paused === true ? true : false,
                      })
                    }
                    onDelete={() => handleRequestDeleteSubscription(sub.id)}
                  />
                ))}
              </>
            )}
          </div>

          {/* List footer */}
          <div className="flex-shrink-0 flex items-center justify-start px-4 py-3">
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                handleAdd();
              }}
              className="p-2 text-sm font-semibold text-primary-button-background rounded hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
            >
              + Add
            </button>
          </div>
        </div>
      )}

      <ConfirmBox
        title="Delete Subscription"
        visible={deleteConfirmSubscriptionId !== null}
        closable
        maskClosable
        width={420}
        cancelText="Cancel"
        okText="Delete"
        okButtonProps={{ danger: true }}
        confirmLoading={deleteSubscriptionMutation.isLoading}
        onCancel={handleCancelDeleteSubscription}
        onOk={handleConfirmDeleteSubscription}
      >
        Are you sure you want to delete this subscription?
      </ConfirmBox>
    </div>
  );
});

SubscribeDashboardModal.displayName = "SubscribeDashboardModal";
